/**
 * 라우트(경로 prefix) → 필요 권한/역할 매핑.
 * 사이드바 메뉴 노출(AdminSidebarNav)과 라우트 가드(RouteAccessGuard)가 공유하는 단일 소스.
 */
export type AccessRequirement = {
  /** 필요한 권한 문자열 (예: "content:read") */
  permission?: string;
  /** 역할 전용 경로 (예: 관리자 관리는 SUPER 전용 — JWT role 클레임 기준) */
  role?: "SUPER";
};

type RouteAccessRule = AccessRequirement & { prefix: string };

const ROUTE_ACCESS_RULES: RouteAccessRule[] = [
  // 분석/통계
  { prefix: "/dashboard" },
  { prefix: "/statistics" },
  { prefix: "/segment-analysis" },
  { prefix: "/marketing-dashboard" },
  { prefix: "/acquisition-report" },
  { prefix: "/coupang-partners" },

  // 사용자
  { prefix: "/users", permission: "user:read" },
  // 지급 요청 — 회원 조회 가능자만 (승인은 user:grant:write로 별도 제어)
  { prefix: "/grant-requests", permission: "user:read" },

  // 컨텐츠 (퀴즈 계열)
  { prefix: "/quiz-packs", permission: "content:read" },
  { prefix: "/battles", permission: "content:read" },
  { prefix: "/tournaments", permission: "content:read" },
  { prefix: "/vocab-words", permission: "content:read" },
  { prefix: "/vocab-ja-review", permission: "content:read" },
  // 문제은행 조회 — 로컬·스테이징에만 있다(사이드바는 /bank/status 로 한 번 더 거른다)
  { prefix: "/bank", permission: "content:read" },
  { prefix: "/curriculums", permission: "content:read" },
  { prefix: "/reward-events", permission: "content:read" },
  { prefix: "/promo-banners", permission: "banner:read" },

  // 광고 시스템
  { prefix: "/ad-dashboard", permission: "ad:read" },
  { prefix: "/ad-advertisers", permission: "ad:read" },
  { prefix: "/ad-campaigns", permission: "ad:read" },
  { prefix: "/ad-groups", permission: "ad:read" },
  { prefix: "/ad-ads", permission: "ad:read" },

  // 고객 지원
  { prefix: "/notices", permission: "notice:read" },
  { prefix: "/faqs", permission: "faq:read" },

  // 스터디그룹
  { prefix: "/study-groups", permission: "study_group:read" },

  // 기부
  { prefix: "/donation-organizations", permission: "donation:read" },
  { prefix: "/donation-campaigns", permission: "donation:read" },

  // 기프티콘
  { prefix: "/giftcon", permission: "giftcon:read" },

  // 단축 URL
  { prefix: "/short-urls" },

  // 설정
  { prefix: "/admins", role: "SUPER" },
  { prefix: "/global-settings", permission: "system:read" },
  { prefix: "/partner-api-keys", permission: "system:read" },
  { prefix: "/test-support", permission: "dev:tools" },
  { prefix: "/versions", permission: "system:read" },
  { prefix: "/service-documents", permission: "document:read" },
];

/** 경로에 매핑된 접근 요구사항을 반환. 매핑이 없으면 null (제한 없음) */
export const getRouteAccess = (pathname: string): AccessRequirement | null => {
  const rule = ROUTE_ACCESS_RULES.find(
    (r) => pathname === r.prefix || pathname.startsWith(`${r.prefix}/`),
  );
  return rule ?? null;
};

/** 보유 권한/역할로 해당 요구사항을 충족하는지 검사 */
export const canAccess = (
  requirement: AccessRequirement | null | undefined,
  permissions: string[],
  backendRole: string | null,
): boolean => {
  if (!requirement) return true;
  if (requirement.role && backendRole !== requirement.role) return false;
  if (requirement.permission && !permissions.includes(requirement.permission)) {
    return false;
  }
  return true;
};

// 권한 없는 경로 진입 시 보낼 대체 경로 후보 — 사이드바 메뉴 순서와 동일
const FALLBACK_CANDIDATES = [
  "/dashboard",
  "/users",
  "/grant-requests",
  "/quiz-packs/list",
  "/promo-banners",
  "/ad-dashboard",
  "/notices",
  "/faqs",
  "/study-groups",
  "/donation-organizations",
  "/giftcon/orders",
  "/short-urls",
  "/global-settings",
];

/** 접근 가능한 첫 페이지를 반환. 최소한 지급 요청(전원 접근 가능)은 항상 접근 가능하다 */
export const findFirstAccessiblePath = (
  permissions: string[],
  backendRole: string | null,
): string => {
  return (
    FALLBACK_CANDIDATES.find((path) =>
      canAccess(getRouteAccess(path), permissions, backendRole),
    ) ?? "/grant-requests"
  );
};
